import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";

import {
  SemifinishedRequirementFlatRow,
  SemifinishedRequirementItem,
  buildMonthsList,
  buildQueryParams,
  pivotSemifinishedRequirement
} from "./pivotSemifinishedRequirement";

interface SemifinishedRequirementReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  items: SemifinishedRequirementItem[];
  months: string[];
}

const state: SemifinishedRequirementReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  items: [],
  months: []
};

function normalizeFilter(params: any) {
  const normalizeIds = (value: any) => {
    if (!value) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  };

  return {
    date_from: params.date_from || null,
    date_to: params.date_to || null,
    products: normalizeIds(params.products),
    semifinisheds: normalizeIds(params.semifinisheds),
    split: params.split === "true"
  };
}

export const REPORT_DATA = "SEMIFINISHED_REQUIREMENT_DATA";

export const actions: ActionTree<SemifinishedRequirementReportState, RootState> = {
  loadData({ commit }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      const filter = normalizeFilter(params);

      if (!filter.date_from || !filter.date_to) {
        console.debug(
          "[SemifinishedRequirementReport.loadData] skip: no period",
          params
        );
        resolve();
        return;
      }

      const queryParams = buildQueryParams(filter);
      const months = buildMonthsList(filter.date_from, filter.date_to);

      console.debug("[SemifinishedRequirementReport.loadData] START", {
        queryParams,
        months
      });

      commit("START_LOADING");

      vm.$db([
        vm
          .$q(REPORT_DATA)
          .raw("reports.semifinished_requirement")
          .action(queryParams)
      ])
        .then((result: ServerResult) => {
          const content = result.getOperation(REPORT_DATA).getContent();
          const rows: SemifinishedRequirementFlatRow[] =
            (content && content.documents) || [];

          console.debug(
            "[SemifinishedRequirementReport.loadData] SUCCESS",
            {
              rows: rows.length,
              months: months.length,
              split: filter.split
            }
          );

          commit("SET_REPORT_DATA", {
            rows,
            months,
            splitBySemifinished: filter.split
          });
          commit("LOADED_SUCCESS");
          resolve();
        })
        .catch((error: any) => {
          console.error(
            "[SemifinishedRequirementReport.loadData] ERROR",
            error
          );
          commit("LOADED_ERROR");
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<SemifinishedRequirementReportState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, { rows, months, splitBySemifinished }) {
    state.months = months;
    state.items = pivotSemifinishedRequirement(
      rows,
      months,
      splitBySemifinished !== false
    );
  }
};

const getters = {};

export const STORE_KEY = "$_semifinished_requirement_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
